import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  DEFAULT_THEME, THEMES, isThemeId, resolveConfig, themeForGrade,
  type Grade, type LevelConfig, type ThemeId,
} from '@numslearn/shared';
import { formatNumber, isRtl, makeTranslate, type Lang, type Numerals, type Translate } from './i18n/index.js';
import { setSoundEnabled } from './sound.js';

/** `auto` follows the grade — the age-adaptive option kept from the bake-off. */
export type ThemeChoice = ThemeId | 'auto';
export type ModeChoice = 'light' | 'dark' | 'auto';
/**
 * How big the numerals are.
 *
 * `large` is the default: a teacher asked for bigger text, and the old size
 * was pitched at fitting a nine-column board on a phone rather than at being
 * read from the back of a classroom. `normal` is the old size, kept for a
 * teacher who would rather have the columns.
 */
export type TextSize = 'normal' | 'large' | 'huge' | 'projector';

export interface Preset {
  id: string;
  name: string;
  grade: Grade;
  level: number;
  overrides: Partial<LevelConfig>;
  theme: ThemeChoice;
}

export interface Settings {
  lang: Lang;
  /**
   * True once a language was actually chosen in the picker. The whole settings
   * object is persisted on every change, so a stored `lang` says nothing about
   * intent — without this flag a browser that merely opened the app once would
   * be pinned to whatever the default was that day. See `load`.
   */
  langPicked: boolean;
  numerals: Numerals;
  themeChoice: ThemeChoice;
  mode: ModeChoice;
  grade: Grade;
  level: number;
  /** Off by default: a room of thirty tablets should stay quiet until asked. */
  sound: boolean;
  textSize: TextSize;
  /** Teacher edits layered over the grade/level defaults. */
  overrides: Partial<LevelConfig>;
  presets: Preset[];
}

const STORAGE_KEY = 'numslearn.settings.v1';

const DEFAULTS: Settings = {
  /* Arabic is the classroom language; English is the second option. */
  lang: 'ar',
  langPicked: false,
  numerals: 'latn',
  themeChoice: 'auto',
  mode: 'auto',
  grade: 2,
  level: 1,
  sound: false,
  textSize: 'large',
  overrides: {},
  presets: [],
};

function load(): Settings {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULTS;
    const parsed = JSON.parse(raw) as Partial<Settings>;
    const merged = { ...DEFAULTS, ...parsed, overrides: parsed.overrides ?? {}, presets: parsed.presets ?? [] };
    // Settings written before Arabic became the default carry an implicit
    // lang: 'en' that nobody asked for; the new default wins until someone
    // picks a language for real. Everything else they set is kept.
    if (!parsed.langPicked) merged.lang = DEFAULTS.lang;
    return merged;
  } catch {
    // A private window, cleared site data, or a browser blocking storage.
    return DEFAULTS;
  }
}

interface Ctx {
  settings: Settings;
  set: <K extends keyof Settings>(key: K, value: Settings[K]) => void;
  setOverride: <K extends keyof LevelConfig>(key: K, value: LevelConfig[K] | undefined) => void;
  resetOverrides: () => void;
  /** The grade/level defaults with the teacher's overrides applied. */
  config: LevelConfig;
  themeId: ThemeId;
  mode: 'light' | 'dark';
  rtl: boolean;
  t: Translate;
  num: (n: number | string) => string;
}

const SettingsCtx = createContext<Ctx | null>(null);

export function SettingsProvider({ children }: { children: ReactNode }) {
  const [settings, setSettings] = useState<Settings>(load);
  const [systemDark, setSystemDark] = useState(
    () => typeof matchMedia !== 'undefined' && matchMedia('(prefers-color-scheme: dark)').matches,
  );

  useEffect(() => {
    const mq = matchMedia('(prefers-color-scheme: dark)');
    const onChange = (e: MediaQueryListEvent) => setSystemDark(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
    } catch {
      /* storage unavailable — settings simply stay session-only */
    }
  }, [settings]);

  const set = useCallback(<K extends keyof Settings>(key: K, value: Settings[K]) => {
    setSettings((s) => ({ ...s, [key]: value, ...(key === 'lang' ? { langPicked: true } : null) }));
  }, []);

  const setOverride = useCallback(<K extends keyof LevelConfig>(key: K, value: LevelConfig[K] | undefined) => {
    setSettings((s) => {
      const next = { ...s.overrides };
      if (value === undefined) delete next[key];
      else next[key] = value;
      return { ...s, overrides: next };
    });
  }, []);

  const resetOverrides = useCallback(() => setSettings((s) => ({ ...s, overrides: {} })), []);

  const themeId: ThemeId =
    settings.themeChoice === 'auto'
      ? themeForGrade(settings.grade)
      : isThemeId(settings.themeChoice) ? settings.themeChoice : DEFAULT_THEME;

  const mode: 'light' | 'dark' =
    settings.mode === 'auto' ? (systemDark ? 'dark' : 'light') : settings.mode;

  const rtl = isRtl(settings.lang);
  const t = useMemo(() => makeTranslate(settings.lang, settings.numerals), [settings.lang, settings.numerals]);
  const num = useCallback((n: number | string) => formatNumber(n, settings.numerals), [settings.numerals]);
  const config = useMemo(
    () => resolveConfig(settings.grade, settings.level, settings.overrides),
    [settings.grade, settings.level, settings.overrides],
  );

  useEffect(() => { setSoundEnabled(settings.sound); }, [settings.sound]);

  useEffect(() => {
    document.documentElement.lang = settings.lang;
    document.documentElement.dir = rtl ? 'rtl' : 'ltr';
    document.documentElement.dataset.mode = mode;
    // One attribute drives every type size in the app, so the numerals, the
    // HUD figures and the buttons grow together — a 48px numeral over a 12px
    // score just moves the problem somewhere else.
    document.documentElement.dataset.textSize = settings.textSize;
    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta) meta.setAttribute('content', THEMES[themeId][mode].bg);
  }, [settings.lang, rtl, mode, themeId, settings.textSize]);

  const value: Ctx = { settings, set, setOverride, resetOverrides, config, themeId, mode, rtl, t, num };
  return <SettingsCtx.Provider value={value}>{children}</SettingsCtx.Provider>;
}

export function useSettings(): Ctx {
  const ctx = useContext(SettingsCtx);
  if (!ctx) throw new Error('useSettings must be used inside <SettingsProvider>');
  return ctx;
}
